import * as React from 'react';
import { ShoppingBag, PiggyBank, BarChart3, History, ArrowDownRight, ArrowUpRight, ArrowRightLeft, BadgeCheck, Target, Gift, Sparkles, Filter, Calendar, Wallet, ArrowRight, Search, LineChart } from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import AccountOverview from './AccountOverview';
import RecentActivity from './RecentActivity';
//import QuickActions from './QuickActions';
import SavingsGoals from './SavingsGoals';
import type { Transaction, SavingsGoal } from '../../types/bank';
import TransferModal from './TransferModal';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import axios from '../../axios-config';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { TransactionTypeName } from '../../types/transactionTypes';

const savingsGoals: SavingsGoal[] = [
  {
    id: 'goal-1',
    name: 'School Trip Fund',
    targetAmount: 1000,
    currentAmount: 750,
    deadline: '2024-05-15',
    icon: <Target className="w-5 h-5 text-blue-500" />
  },
  {
    id: 'goal-2',
    name: 'Art Supplies Kit',
    targetAmount: 400,
    currentAmount: 280,
    deadline: '2024-04-01',
    icon: <Gift className="w-5 h-5 text-pink-500" />
  },
  {
    id: 'goal-3',
    name: 'Science Fair Project',
    targetAmount: 300,
    currentAmount: 150,
    deadline: '2024-04-30',
    icon: <Sparkles className="w-5 h-5 text-purple-500" />
  }
];

export default function Bank() {
  // Destructure setUserProfile along with userProfile from context
  const { userProfile, setUserProfile } = React.useContext(UserContext);
  const [acornTransactions, setAcornTransactions] = React.useState<Transaction[]>([]);
  const [activeTab, setActiveTab] = React.useState<'overview' | 'savings' | 'transfers' | 'history' | 'chart'>('overview');
  const [showTransferModal, setShowTransferModal] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(false);
  const [filters, setFilters] = React.useState({
    type: 'all',
    minAmount: '',
    maxAmount: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [transferType, setTransferType] = React.useState<'all' | 'sent' | 'received'>('all');
  const [transferDirection, setTransferDirection] = React.useState<'toSavings' | 'toGeneral'>('toSavings');
  const [transferAmount, setTransferAmount] = React.useState('');

  React.useEffect(() => {
    // Using a type coercion since 'accounts' is not defined on UserProfile.
    const accounts = (userProfile as any)?.accounts;
    if (!accounts) return;

    // Simple ID-to-type mapper
    const typeMap: Record<number, Transaction['type']> = {
      1: 'withdrawal',
      2: 'reward',
      3: 'deposit',
      4: 'transfer',
      5: 'purchase',
      8: 'deposit', // Example fallback
      9: 'transfer',
      10: 'transfer'
    };

    const allTx = accounts.flatMap((acc: any) =>
      acc.acornTransactions?.map((t: any) => ({
        id: `txn-${t.acornTransactionId}`,
        type: typeMap[t.acornTransactionTypeId] || 'deposit',
        amount: t.points,
        description: t.acornTransactionTypeName ?? `${typeMap[t.acornTransactionTypeId] ?? 'deposit'} transaction`,
        date: (() => {
          const d = new Date(t.createdAt);
          return isNaN(d.getTime()) ? '' : d.toISOString();
        })(),
        category: acc.accountTypeName ?? 'Account',
        status: 'completed',
        accountTypeId: acc.accountTypeId,  // using the flattened field from JSON
        transferDirection: t.acornTransactionTypeName === TransactionTypeName.TransferToSavings
          ? 'toSavings'
          : t.acornTransactionTypeName === TransactionTypeName.TransferFromSavings
            ? 'toGeneral'
            : null
      })) || []
    );
    setAcornTransactions(allTx.sort((a: Transaction, b: Transaction) => new Date(b.date).getTime() - new Date(a.date).getTime()));
  }, [userProfile]);

  const filteredTransactions = React.useMemo(() => {
    return acornTransactions.filter(transaction => {
      // Type filter
      if (filters.type !== 'all' && transaction.type !== filters.type) return false;

      // Amount range filter
      const amount = Math.abs(transaction.amount);
      if (filters.minAmount && amount < Number(filters.minAmount)) return false;
      if (filters.maxAmount && amount > Number(filters.maxAmount)) return false;

      // Date range filter
      const transactionDate = new Date(transaction.date);
      if (filters.startDate && transactionDate < new Date(filters.startDate)) return false;
      if (filters.endDate && transactionDate > new Date(filters.endDate)) return false;

      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return (
          transaction.description.toLowerCase().includes(searchLower) ||
          transaction.category.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }, [acornTransactions, filters]);

  // Add sort in descending order with typed parameters
  const sortedFiltered = filteredTransactions.slice().sort((a: Transaction, b: Transaction) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const getTransactionIcon = (type: Transaction['type']) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownRight className="w-4 h-4 text-green-500" />;
      case 'withdrawal':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'transfer':
        return <ArrowRightLeft className="w-4 h-4 text-blue-500" />;
      case 'reward':
        return <BadgeCheck className="w-4 h-4 text-purple-500" />;
      case 'purchase':
        return <ShoppingBag className="w-4 h-4 text-orange-500" />;
      default:
        return null;
    }
  };

  const getTransactionColor = (type: Transaction['type']) => {
    switch (type) {
      case 'deposit':
        return 'text-green-600 bg-green-50';
      case 'withdrawal':
        return 'text-red-600 bg-red-50';
      case 'transfer':
        return 'text-blue-600 bg-blue-50';
      case 'reward':
        return 'text-purple-600 bg-purple-50';
      case 'purchase':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  // Prepare chart data
  const chartData = React.useMemo(() => {
    const sortedTransactions = [...acornTransactions].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Calculate the starting balances for each account:
    const initialGeneral =
      (userProfile?.general || 0) -
      sortedTransactions
        .filter(tx => tx.accountTypeId === 1)
        .reduce((sum, tx) => sum + tx.amount, 0);
    const initialSavings =
      (userProfile?.savings || 0) -
      sortedTransactions
        .filter(tx => tx.accountTypeId === 2)
        .reduce((sum, tx) => sum + tx.amount, 0);

    let generalBalance = initialGeneral;
    let savingsBalance = initialSavings;
    const dailyData: Array<{ date: string; general: number; savings: number }> = [];
    let lastDay: string | null = null;

    sortedTransactions.forEach(tx => {
      const d = new Date(tx.date);
      d.setHours(0, 0, 0, 0);
      if (tx.accountTypeId === 1) generalBalance += tx.amount;
      if (tx.accountTypeId === 2) savingsBalance += tx.amount;

      const dayKey = d.toISOString();
      if (dayKey !== lastDay) {
        dailyData.push({
          date: d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }), general: parseFloat(generalBalance.toFixed(2)), savings: parseFloat(savingsBalance.toFixed(2))
        });
        lastDay = dayKey;
      } else {
        const lastEntry = dailyData[dailyData.length - 1];
        lastEntry.general = parseFloat(generalBalance.toFixed(2));
        lastEntry.savings = parseFloat(savingsBalance.toFixed(2));
      }
    });

    return dailyData;
  }, [acornTransactions, userProfile?.general, userProfile?.savings]);

  const confirmTransfer = (message: string): Promise<boolean> =>
    new Promise(resolve => {
      confirmAlert({
        title: 'Confirm Transfer',
        message,
        buttons: [
          { label: 'Yes', onClick: () => resolve(true) },
          { label: 'No', onClick: () => resolve(false) }
        ],
        closeOnEscape: true,
        closeOnClickOutside: true
      });
    });

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fromAccount = transferDirection === 'toSavings' ? 'General' : 'Savings';
    const toAccount = transferDirection === 'toSavings' ? 'Savings' : 'General';
    const message = `I would like to transfer ${transferAmount} point(s) from my ${fromAccount} to my ${toAccount} account.`;
    const isConfirmed = await confirmTransfer(message);

    if (!isConfirmed) return;
    try {
      const endpoint = transferDirection === 'toSavings'
        ? '/api/Account/Transfer/Savings/To'
        : '/api/Account/Transfer/Savings/From';

      const response = await axios.post(endpoint, {
        studentId: userProfile?.userId,
        points: parseFloat(transferAmount).toFixed(2)
      });
      // Extract the returned balances and update the UI via UserContext
      const { balanceGeneral, balanceSavings } = response.data;
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          userId: userProfile.userId ?? null,
          general: balanceGeneral,
          savings: balanceSavings
        });
      }
      setTransferAmount('');

      toast.success('Transfer successful.');
    } catch (error) {
      console.error(error);
      toast.error('Transfer failed. Please try again.');
    }
  };

  return (
    <div>
      {/* Bank Header */}
      <div className="max-w-6xl mx-auto px-6 pt-0">

        {/* Bank Header */}
        <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 p-6 mt-0">

          {/* Tab Navigation */}
          <div className="mt-0 border-b border-gray-200/50">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'overview'
                  ? 'text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Overview
                </div>
                {activeTab === 'overview' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                )}
              </button>
              {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                <button
                  onClick={() => setActiveTab('savings')}
                  className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'savings'
                    ? 'text-green-600'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >

                  <div className="flex items-center gap-2">
                    <PiggyBank className="w-4 h-4" />
                    Savings Goals
                  </div>

                  {activeTab === 'savings' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
                  )}
                </button>
              )}
              <button
                onClick={() => setActiveTab('transfers')}
                className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'transfers'
                  ? 'text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}             >
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4" />
                  Transfers
                </div>
                {activeTab === 'transfers' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'history'
                  ? 'text-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4" />
                  History
                </div>
                {activeTab === 'history' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('chart')}
                className={`px-4 py-2 font-medium text-sm transition-colors relative ${activeTab === 'chart'
                  ? 'text-green-600'
                  : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <LineChart className="w-4 h-4" />
                  Chart
                </div>
                {activeTab === 'chart' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Account Overview */}
            <AccountOverview />

            {/* Recent Activity */}
            <RecentActivity
              transactions={acornTransactions}
              getTransactionIcon={getTransactionIcon}
              getTransactionColor={getTransactionColor}
            />

            {/* Quick Actions */}
            {/* <QuickActions onTransfer={() => setShowTransferModal(true)} /> */}

            {/* Transfer Modal */}
            {showTransferModal && (
              <TransferModal
                accounts={[
                  { id: 'general', name: 'General Account', balance: 2450, icon: null, color: '' },
                  { id: 'savings', name: 'Savings Account', balance: 1180, icon: null, color: '' }
                ]}
                onTransfer={(_fromId, _toId, _amount) => {
                  // Handle transfer logic here (unused parameters)
                  setShowTransferModal(false);
                }}
                onClose={() => setShowTransferModal(false)}
              />
            )}
          </div>
        )}

        {activeTab === 'savings' && (
          <div className="space-y-6">
            {/* Savings Goals */}
            <SavingsGoals goals={savingsGoals} />
          </div>
        )}

        {activeTab === 'transfers' && (
          <div className="space-y-6">
            {/* Account Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Wallet className="w-5 h-5 text-blue-500" />
                  <h3 className="font-semibold text-gray-800">General Account</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900">{(userProfile?.general || 0).toLocaleString()} pts</p>
                <p className="text-sm text-gray-500 mt-1">Available to spend</p>
              </div>
              <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <PiggyBank className="w-5 h-5 text-green-500" />
                  <h3 className="font-semibold text-gray-800">Savings Account</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900">{(userProfile?.savings || 0).toLocaleString()} pts</p>
                <p className="text-sm text-gray-500 mt-1">Total saved</p>
              </div>
            </div>

            {/* Transfer Form */}
            <form onSubmit={handleTransferSubmit} className="grid grid-cols-[1fr,auto,1fr] gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {transferDirection === 'toSavings'
                    ? `From` : `To`}
                </label>
                General Account (${(userProfile?.general || 0).toLocaleString()} pts)
              </div>

              <div className="flex items-center justify-center pb-2">
                <button
                  type="button"
                  onClick={() => setTransferDirection(prev => prev === 'toSavings' ? 'toGeneral' : 'toSavings')}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Toggle transfer direction"
                  title={transferDirection === 'toSavings'
                    ? "Transfer from General to Savings Account"
                    : "Transfer from Savings to General Account"}
                >
                  {transferDirection === 'toSavings' ? (
                    <ArrowRight className="w-9 h-9 text-gray-800 font-bold" />
                  ) : (
                    <ArrowRight className="w-9 h-9 text-gray-800 font-bold transform rotate-180" />
                  )}
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {transferDirection === 'toSavings'
                    ? `To`
                    : `From`}
                </label>
                Savings Account (${(userProfile?.savings || 0).toLocaleString()} pts)
              </div>
            </form>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount
              </label>
              <div className="flex gap-4">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="Enter amount to transfer"
                />
                <button
                  type="submit"
                  onClick={handleTransferSubmit} // new direct click binding
                  className="bg-gray-800 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors flex items-center gap-2"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  Transfer
                </button>
              </div>
            </div>

            {/* Transfer Actions */}
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setTransferType('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${transferType === 'all'
                    ? 'bg-gray-800 text-white'
                    : 'bg-white/50 text-gray-600 hover:bg-white'
                    }`}
                >
                  All Transfers
                </button>
                <button
                  onClick={() => setTransferType('sent')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${transferType === 'sent'
                    ? 'bg-gray-800 text-white'
                    : 'bg-white/50 text-gray-600 hover:bg-white'
                    }`}
                >
                  To Savings
                </button>
                <button
                  onClick={() => setTransferType('received')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${transferType === 'received'
                    ? 'bg-gray-800 text-white'
                    : 'bg-white/50 text-gray-600 hover:bg-white'
                    }`}
                >
                  To General
                </button>
              </div>
            </div>

            {/* Transfer History */}
            <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-800">Transfer History</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    Last 30 days
                  </div>
                </div>
                {acornTransactions
                  .filter(t => t.type === 'transfer')
                  .filter(t => {
                    if (transferType === 'sent') return t.transferDirection === 'toSavings';
                    if (transferType === 'received') return t.transferDirection === 'toGeneral';
                    return true;
                  }).length > 0 ? (
                  <div className="space-y-4">
                    {acornTransactions
                      .filter(t => t.type === 'transfer')
                      .filter(t => {
                        if (transferType === 'sent') return t.transferDirection === 'toSavings';
                        if (transferType === 'received') return t.transferDirection === 'toGeneral';
                        return true;
                      })
                      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                      .map(transaction => (
                        <div
                          key={transaction.id}
                          className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${getTransactionColor(transaction.type)}`}>
                              {getTransactionIcon(transaction.type)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{transaction.description}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-sm text-gray-500">{transaction.category}</span>
                                <span className="text-sm text-gray-400">•</span>
                                <span className="text-sm text-gray-500">
                                  {new Date(transaction.date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-medium ${transaction.transferDirection === 'toGeneral' ? 'text-green-600' : 'text-red-600'}`}>
                              {transaction.transferDirection === 'toGeneral' ? '+' : ''}{Math.abs(transaction.amount)} pts
                            </p>
                            <p className="text-sm text-gray-500">
                              {transaction.transferDirection === 'toSavings' ? 'To Savings' : 'To General'}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No transfer history found</p>
                    <p className="text-sm text-gray-400 mt-1">Try making a transfer between your accounts</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6">
            {/* Filters Header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    placeholder="Search transactions..."
                    className="w-full pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-colors"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-white/50 hover:bg-white text-gray-700 rounded-lg transition-colors"
              >
                <Filter className="w-4 h-4" />
                <span className="font-medium text-sm">Filters</span>
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-gray-800">Advanced Filters</h3>
                  <button
                    onClick={() => {
                      setFilters({
                        type: 'all',
                        minAmount: '',
                        maxAmount: '',
                        search: '',
                        startDate: '',
                        endDate: ''
                      });
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Reset Filters
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction Type
                    </label>
                    <select
                      value={filters.type}
                      onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-colors"
                    >
                      <option value="all">All Types</option>
                      <option value="deposit">Deposits</option>
                      <option value="withdrawal">Withdrawals</option>
                      <option value="transfer">Transfers</option>
                      <option value="reward">Rewards</option>
                      <option value="purchase">Purchases</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Amount
                    </label>
                    <input
                      type="number"
                      value={filters.minAmount}
                      onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                      placeholder="0"
                      className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Amount
                    </label>
                    <input
                      type="number"
                      value={filters.maxAmount}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                      placeholder="1000"
                      className="w-full px-3 py-2 bg-white/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-transparent transition-colors"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Transactions List */}
            <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Transaction History</h3>
                  <span className="text-sm text-gray-500">{sortedFiltered.length} transactions</span>
                </div>
                <div className="space-y-4">
                  {sortedFiltered.map(transaction => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${getTransactionColor(transaction.type)}`}>
                          {getTransactionIcon(transaction.type)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{transaction.description}</p>
                          <p className="text-sm text-gray-500">{transaction.category}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-medium ${transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount} pts
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(transaction.date).toLocaleDateString('en-US', {
                            year: '2-digit',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'chart' && (
          <div className="space-y-6">
            <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Account Balance History</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  Transaction History
                </div>
              </div>

              {chartData.length > 0 ? (
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorGeneral" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#8884d8" stopOpacity={0.1} />
                        </linearGradient>
                        <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tickFormatter={dateLabel =>
                          new Date(dateLabel).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })
                        }
                      />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="general"
                        stroke="#8884d8"
                        fillOpacity={1}
                        fill="url(#colorGeneral)"
                        name="General Account"
                      />
                      <Area
                        type="monotone"
                        dataKey="savings"
                        stroke="#82ca9d"
                        fillOpacity={1}
                        fill="url(#colorSavings)"
                        name="Savings Account"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No transaction data available to display chart</p>
                </div>
              )}
            </div>

            <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Transaction Summary</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-white/70 rounded-lg">
                  <p className="text-sm text-gray-500">Total Deposits</p>
                  <p className="text-xl font-bold text-green-600">
                    +{acornTransactions
                      .filter(tx => tx.amount > 0)
                      .reduce((sum, tx) => sum + tx.amount, 0)
                      .toLocaleString()} pts
                  </p>
                </div>
                <div className="p-4 bg-white/70 rounded-lg">
                  <p className="text-sm text-gray-500">Total Withdrawals</p>
                  <p className="text-xl font-bold text-red-600">
                    {acornTransactions
                      .filter(tx => tx.amount < 0)
                      .reduce((sum, tx) => sum + tx.amount, 0)
                      .toLocaleString()} pts
                  </p>
                </div>
                <div className="p-4 bg-white/70 rounded-lg">
                  <p className="text-sm text-gray-500">Current Balance</p>
                  <p className="text-xl font-bold text-blue-600">
                    {(userProfile?.general || 0).toLocaleString()} pts
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}